import { useEffect, useRef, useState } from 'react';

import { faEdit, faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, Label } from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';

import type { ZuubOnboardingBeginResponse } from '@/@types/zuub/contracts';

import { CopyButton } from '../../components/CopyButton';
import { formatPhoneNumber } from '../helpers/helpers';

import { SecurityQuestionsSection } from './securityQuestions/SecurityQuestionsSection';

interface VideaUserInformationFormProps {
  onboardingData?: ZuubOnboardingBeginResponse;
  localPassword?: string;
  isSaving?: boolean;
  payerId?: number;
  onSavePassword?: (password: string) => Promise<void> | void;
  onSaveUsername?: (username: string) => Promise<void> | void;
  onSaveQuestions?: (
    payerId: number,
    questions: Array<{ question: string; answer: string }>,
  ) => Promise<void>;
}

export const VideaUserInformationForm = ({
  onboardingData,
  localPassword,
  isSaving = false,
  payerId,
  onSavePassword,
  onSaveUsername,
  onSaveQuestions,
}: VideaUserInformationFormProps) => {
  const { t } = useLingui();

  const editingFieldActiveStyles =
    '!vai-border-blue-500 focus-visible:vai-ring-2 focus-visible:vai-ring-blue-500 focus-visible:vai-border-blue-500';

  const [editingField, setEditingField] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isCopySuccess, setIsCopySuccess] = useState<string | null>(null);
  const [passwordValue, setPasswordValue] = useState(
    localPassword || onboardingData?.password || '',
  );
  const [tempPasswordValue, setTempPasswordValue] = useState(passwordValue);
  const [usernameValue, setUsernameValue] = useState(
    onboardingData?.username || '',
  );
  const [tempUsernameValue, setTempUsernameValue] = useState(usernameValue);

  // Track the last known server username to detect actual server changes
  const lastServerUsernameRef = useRef(onboardingData?.username);

  const handleStartEdit = (fieldId: string) => {
    // Ensure only one field is edited at a time
    // If another field is being edited, cancel it first
    if (editingField && editingField !== fieldId) {
      handleCancelEdit();
    }
    setEditingField(fieldId);
    if (fieldId === 'password') {
      setTempPasswordValue(passwordValue);
    } else if (fieldId === 'username') {
      setTempUsernameValue(usernameValue);
    }
  };

  const handleCancelEdit = () => {
    const currentField = editingField;
    setEditingField(null);
    if (currentField === 'password') {
      setTempPasswordValue(passwordValue);
    } else if (currentField === 'username') {
      setTempUsernameValue(usernameValue);
    }
  };

  const handleSavePassword = async () => {
    if (onSavePassword && tempPasswordValue) {
      await onSavePassword(tempPasswordValue);
      setPasswordValue(tempPasswordValue);
    }
    setEditingField(null);
  };

  const handleSaveUsername = async () => {
    if (onSaveUsername && tempUsernameValue) {
      await onSaveUsername(tempUsernameValue);
      setUsernameValue(tempUsernameValue);
    }
    setEditingField(null);
  };

  const handleCopy = async (value: string, fieldId: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopySuccess(fieldId);
      // Reset the success state after 2 seconds
      setTimeout(() => setIsCopySuccess(null), 2000);
    } catch (err) {
      console.error(`Failed to copy ${fieldId}:`, err);
    }
  };

  // Reset temp value when editing is cancelled
  useEffect(() => {
    if (editingField === null) {
      setTempPasswordValue(passwordValue);
      setTempUsernameValue(usernameValue);
    }
  }, [editingField, passwordValue, usernameValue]);

  // Update password value when localPassword changes
  useEffect(() => {
    if (localPassword) {
      setPasswordValue(localPassword);
      setTempPasswordValue(localPassword);
    }
  }, [localPassword]);

  // Update username value when onboardingData changes from the server
  useEffect(() => {
    // Only sync if the server value actually changed (not just on editingField change)
    if (
      onboardingData?.username !== undefined &&
      onboardingData.username !== lastServerUsernameRef.current
    ) {
      // Only update local state and ref if not currently editing
      // If editing, we defer the ref update until editing is done to avoid missing the sync
      if (editingField !== 'username') {
        lastServerUsernameRef.current = onboardingData.username;
        setUsernameValue(onboardingData.username);
        setTempUsernameValue(onboardingData.username);
      }
    }
  }, [onboardingData?.username, editingField]);

  return (
    <div className="vai-bg-zinc-50 vai-border vai-border-border vai-rounded-lg vai-p-6">
      <div className="vai-flex vai-items-center vai-justify-between vai-mb-4 vai-h-10">
        <h3 className=" vai-font-semibold vai-mr-2">Videa User Information</h3>
      </div>

      <div className="vai-space-y-4">
        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          {/* First Name */}
          <div className="vai-group">
            <Label htmlFor="firstName">
              <Trans>First Name</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="firstName"
                type="text"
                value="Insurance"
                readOnly
                className="vai-pr-10"
              />
              <CopyButton
                value="Insurance"
                fieldId="firstName"
                isCopied={isCopySuccess === 'firstName'}
                onCopy={handleCopy}
                copyLabel={t`Copy first name`}
              />
            </div>
          </div>
          {/* Last Name */}
          <div className="vai-group">
            <Label htmlFor="lastName">
              <Trans>Last Name</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="lastName"
                type="text"
                value="Admin"
                readOnly
                className="vai-pr-10"
              />
              <CopyButton
                value="Admin"
                fieldId="lastName"
                isCopied={isCopySuccess === 'lastName'}
                onCopy={handleCopy}
                copyLabel={t`Copy last name`}
              />
            </div>
          </div>
        </div>

        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          {/* Date of Birth */}
          <div className="vai-group">
            <Label htmlFor="dateOfBirth">
              <Trans>Date of Birth</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="dateOfBirth"
                type="date"
                // Hardcoded. Same for all accounts.
                value="1980-01-01"
                readOnly
                className="vai-pr-10"
              />
              <CopyButton
                value="1980-01-01"
                fieldId="dateOfBirth"
                isCopied={isCopySuccess === 'dateOfBirth'}
                onCopy={handleCopy}
                copyLabel={t`Copy date of birth`}
              />
            </div>
          </div>
          {/* Phone */}
          <div className="vai-group">
            <Label htmlFor="phone">
              <Trans>Phone</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="phone"
                type="tel"
                value={formatPhoneNumber(onboardingData?.phoneNumber)}
                readOnly
                className="vai-pr-10"
              />
              <CopyButton
                value={onboardingData?.phoneNumber || ''}
                fieldId="phone"
                isCopied={isCopySuccess === 'phone'}
                onCopy={handleCopy}
                copyLabel={t`Copy phone`}
              />
            </div>
          </div>
        </div>

        {/* Email Address */}
        <div className="vai-grid vai-grid-cols-2 vai-gap-4">
          <div className="vai-group">
            <Label htmlFor="email">
              <Trans>Email Address</Trans>
            </Label>
            <div className="vai-relative">
              <Input
                id="email"
                type="email"
                value={onboardingData?.email}
                readOnly
                className="vai-pr-10"
              />
              <CopyButton
                value={onboardingData?.email || ''}
                fieldId="email"
                isCopied={isCopySuccess === 'email'}
                onCopy={handleCopy}
                copyLabel={t`Copy email`}
              />
            </div>
          </div>
        </div>

        {/* Username & Password Section */}
        <div className="vai-mt-8 vai-pt-6">
          <h4 className="vai-font-semibold vai-mb-4">
            <Trans>Portal Credentials</Trans>
          </h4>
          <div className="vai-grid vai-grid-cols-2 vai-gap-4">
            {/* Username */}
            <div className="vai-group">
              <Label htmlFor="username">
                <Trans>Username</Trans>
              </Label>
              <div className="vai-flex vai-items-center vai-gap-2">
                <div className="vai-relative vai-flex-1">
                  <Input
                    id="username"
                    type="text"
                    value={
                      editingField === 'username'
                        ? tempUsernameValue
                        : usernameValue
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTempUsernameValue(e.target.value)
                    }
                    placeholder={t`Enter username`}
                    readOnly={editingField !== 'username'}
                    className={cn(
                      'vai-pr-20',
                      editingField === 'username' && editingFieldActiveStyles,
                    )}
                  />
                  <div className="vai-absolute vai-right-2 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-flex vai-gap-1">
                    {editingField !== 'username' && (
                      <>
                        <CopyButton
                          value={usernameValue}
                          fieldId="username"
                          isCopied={isCopySuccess === 'username'}
                          onCopy={handleCopy}
                          copyLabel={t`Copy username`}
                          isAbsolutePositioned={false}
                        />
                        <button
                          type="button"
                          className="vai-w-8 vai-h-8 vai-flex vai-items-center vai-justify-center hover:vai-bg-gray-100 vai-rounded vai-opacity-0 group-hover:vai-opacity-100 vai-transition-opacity"
                          onClick={() => handleStartEdit('username')}
                          title={t`Edit username`}
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            className="vai-w-4 vai-h-4 vai-text-muted-foreground"
                          />
                        </button>
                      </>
                    )}
                    {editingField === 'username' && (
                      <CopyButton
                        value={tempUsernameValue}
                        fieldId="username"
                        isCopied={isCopySuccess === 'username'}
                        onCopy={handleCopy}
                        copyLabel={t`Copy username`}
                        isAbsolutePositioned={false}
                      />
                    )}
                  </div>
                </div>
              </div>
              {editingField === 'username' && (
                <div className="vai-flex vai-gap-2 vai-mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelEdit}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveUsername}
                    disabled={isSaving || !tempUsernameValue}
                  >
                    {isSaving ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
                  </Button>
                </div>
              )}
            </div>

            {/* Password */}
            <div className="vai-group">
              <Label htmlFor="password">
                <Trans>Password</Trans>
              </Label>
              <div className="vai-flex vai-items-center vai-gap-2">
                <div className="vai-relative vai-flex-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={
                      editingField === 'password'
                        ? tempPasswordValue
                        : passwordValue
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTempPasswordValue(e.target.value)
                    }
                    placeholder={t`Enter password`}
                    readOnly={editingField !== 'password'}
                    className={cn(
                      'vai-pr-20',
                      editingField === 'password' && editingFieldActiveStyles,
                    )}
                  />
                  <div className="vai-absolute vai-right-2 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-flex vai-gap-1">
                    {editingField !== 'password' && (
                      <>
                        <CopyButton
                          value={passwordValue}
                          fieldId="password"
                          isCopied={isCopySuccess === 'password'}
                          onCopy={handleCopy}
                          copyLabel={t`Copy password`}
                          isAbsolutePositioned={false}
                        />
                        <button
                          type="button"
                          className="vai-w-8 vai-h-8 vai-flex vai-items-center vai-justify-center hover:vai-bg-gray-100 vai-rounded vai-opacity-0 group-hover:vai-opacity-100 vai-transition-opacity"
                          onClick={() => handleStartEdit('password')}
                          title={t`Edit password`}
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            className="vai-w-4 vai-h-4 vai-text-muted-foreground"
                          />
                        </button>
                        <button
                          type="button"
                          className="vai-w-8 vai-h-8 vai-flex vai-items-center vai-justify-center hover:vai-bg-gray-100 vai-rounded"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            className="vai-w-4 vai-h-4 vai-text-muted-foreground"
                          />
                        </button>
                      </>
                    )}
                    {editingField === 'password' && (
                      <>
                        <CopyButton
                          value={tempPasswordValue}
                          fieldId="password"
                          isCopied={isCopySuccess === 'password'}
                          onCopy={handleCopy}
                          copyLabel={t`Copy password`}
                          isAbsolutePositioned={false}
                        />
                        <button
                          type="button"
                          className="vai-w-8 vai-h-8 vai-flex vai-items-center vai-justify-center hover:vai-bg-gray-100 vai-rounded"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            className="vai-w-4 vai-h-4 vai-text-muted-foreground"
                          />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {editingField === 'password' && (
                <div className="vai-flex vai-gap-2 vai-mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelEdit}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSavePassword}
                    disabled={isSaving || !tempPasswordValue}
                  >
                    {isSaving ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <p className="vai-mt-3 vai-text-sm vai-text-muted-foreground">
            <Trans>
              If the username we generated is not accepted by the portal, adjust
              it to fit their unique requirements. Then, edit the username in
              this form to ensure that it stays consistent and accurate.
            </Trans>
          </p>
        </div>

        {/* Security Questions */}
        <SecurityQuestionsSection
          payerId={payerId}
          isSaving={isSaving}
          onSaveQuestions={onSaveQuestions}
        />
      </div>
    </div>
  );
};
